import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { MakeAppointmentExchangeOfferRequest, appointmentExchangesApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface MakeOfferParams {
  exchangeId: string;
  request: MakeAppointmentExchangeOfferRequest;
}

interface WithdrawOfferParams {
  exchangeId: string;
  offerId: string;
}

/**
 * Making and withdrawing a counter-offer. Both refresh the exchanges prefix, which covers the list,
 * the sidebar badge and the exchangeable Termine of an open picker. A failure also refreshes, so a
 * row that went stale (e.g. a 409 because the offer already exists) shows its real state again.
 */
export function useExchangeOfferMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: [QueryKey.AppointmentExchanges] });

  const makeOffer = useMutation({
    mutationFn: ({ exchangeId, request }: MakeOfferParams) =>
      appointmentExchangesApi.makeAppointmentExchangeOffer({
        id: exchangeId,
        makeAppointmentExchangeOfferRequest: request,
      }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("exchange.offerDialog.successMessage"));
    },
    onError: invalidate,
  });

  const withdrawOffer = useMutation({
    mutationFn: ({ exchangeId, offerId }: WithdrawOfferParams) =>
      appointmentExchangesApi.withdrawAppointmentExchangeOffer({ id: exchangeId, offerId }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("exchange.withdrawOfferDialog.successMessage"));
    },
    onError: invalidate,
  });

  return { makeOffer, withdrawOffer, isBusy: makeOffer.isPending || withdrawOffer.isPending };
}
